import * as p from '@clack/prompts'
import type { PostGenerateOptions, PostGenerateResult } from '@cli-types/next-adapter/generators.js'
import { FORMAT_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { detectPackageManager } from '@cli-utils/process.js'
import { spinner } from '@cli-utils/terminal.js'
import { runDrizzlePush } from '@next-adapter/drizzle-push/run-drizzle-push.js'
import { printNextSteps } from '@next-adapter/next-steps.js'
import { hasPkgScript } from '@next-adapter/package-json/has-pkg-script.js'
import { ensureDatabasePushDependencies } from './ensure-database-push-dependencies.js'
import { loadEnvFile } from './load-env-file.js'
import { runOxfmtCheckWrite } from './run-oxfmt-check-write.js'
import { runPmScript } from './run-pm-script.js'

export async function runPostGenerate(
  cwd: string,
  schemaName: string,
  options: PostGenerateOptions = {}
): Promise<PostGenerateResult> {
  const pm = detectPackageManager(cwd)
  const result: PostGenerateResult = {
    dbPush: 'skipped',
    lintFix: 'skipped'
  }

  if (options.hasConflicts) {
    result.dbPush = 'skipped'
    result.lintFix = 'skipped'

    const lines = [
      `Database: skipped (resolve conflict markers in ${options.conflictPaths?.length ?? 0} file(s), then re-run)`,
      'Formatting: skipped (oxfmt refuses files with conflict markers)',
      ...(options.conflictPaths ?? []).map((conflictPath) => `  - ${conflictPath}`)
    ]
    p.log.warn(lines.join('\n'))
    p.log.message(
      'Resolve markers in the files above and re-run the BetterStart command that wrote them. Post-write tasks run automatically once every file is clean.'
    )

    return result
  }

  if (!options.skipMigration) {
    loadEnvFile(cwd)

    const dbUrl = process.env.DATABASE_URL

    if (!dbUrl) {
      result.dbPush = 'no-db-url'
      p.log.warn(
        [
          'Database: skipped (no DATABASE_URL configured)',
          '  To sync later: run db:push after setting DATABASE_URL'
        ].join('\n')
      )
    } else if (!(await ensureDatabasePushDependencies(cwd, pm))) {
      result.dbPush = 'failed'
      p.log.warn(
        'Database push failed (install database dependencies and run drizzle-kit push manually)'
      )
    } else if (hasPkgScript(cwd, 'db:push')) {
      const s = spinner()
      s.start('Pushing database schema')
      const ok = await runPmScript(pm, 'db:push', cwd)
      result.dbPush = ok ? 'success' : 'failed'
      if (ok) {
        s.stop('Database schema synced')
      } else {
        // clack styles stop() as a success; clear it so the warning below is
        // the single, correctly-styled failure line.
        s.clear()
        p.log.warn('Database push failed (run db:push manually)')
      }
    } else {
      // The spinner is cleared the moment drizzle prints, so its interactive
      // rename prompts — which need the terminal — render on a clean line.
      const s = spinner()
      let spinnerVisible = true
      const clearSpinner = () => {
        if (!spinnerVisible) return
        spinnerVisible = false
        s.clear()
      }
      s.start('Pushing database schema')
      try {
        const pushResult = await runDrizzlePush(cwd, {
          interactive: true,
          onOutput: clearSpinner
        })
        if (!pushResult.success) {
          throw new Error(pushResult.error ?? 'drizzle-kit push failed')
        }
        result.dbPush = 'success'
        if (spinnerVisible) {
          spinnerVisible = false
          s.stop('Database schema synced')
        } else {
          p.log.success('Database schema synced')
        }
      } catch {
        result.dbPush = 'failed'
        clearSpinner()
        p.log.warn('Database push failed (run drizzle-kit push manually)')
      }
    }
  } else {
    p.log.info(`Database: skipped (${options.skipMigrationMessage ?? '--skip-migration'})`)
  }

  const formatSpinner = spinner()
  formatSpinner.start('Formatting generated files')
  if (hasPkgScript(cwd, 'lint:fix')) {
    const ok = await runPmScript(pm, 'lint:fix', cwd, FORMAT_TIMEOUT_MS)
    result.lintFix = ok ? 'success' : 'failed'
    if (ok) {
      formatSpinner.stop('Code formatted')
    } else {
      formatSpinner.clear()
      p.log.warn('Lint fix had issues (run lint:fix manually)')
    }
  } else {
    try {
      if (!(await runOxfmtCheckWrite(cwd))) {
        throw new Error('oxfmt binary not found')
      }
      result.lintFix = 'success'
      formatSpinner.stop('Code formatted with oxfmt')
    } catch {
      result.lintFix = 'failed'
      formatSpinner.clear()
      p.log.warn('Formatting had issues (run oxfmt manually)')
    }
  }

  if (options.showNextSteps !== false) {
    const schemaRoute =
      options.schemaRoutePath ?? `${options.adminRoutePath ?? '/admin'}/${schemaName}`
    printNextSteps({
      reviewLabel: 'the generated files',
      needsMigration: Boolean(options.skipMigration) || result.dbPush !== 'success',
      route: schemaRoute
    })
  }

  return result
}
